import React, { useState } from "react";
import { CheckCircle, Warning, Barcode } from "@phosphor-icons/react";
import { toast } from "sonner";
import { teleproApi } from "../lib/teleproApi";

/**
 * Petit encart pour afficher / mettre à jour le statut SIRET du télépro.
 *
 * Cahier de modifs 2026-02 :
 * - Choix « J'ai déjà un SIRET » ou « Immatriculation en cours »
 * - Signature autorisée sans SIRET ; relance dès attribution
 * - Le versement des commissions n'est PAS bloqué (choix produit du 2026-02)
 *   mais la transmission reste un engagement contractuel.
 */
export default function TeleproSiretCard({ me, onUpdated }) {
  const [editing, setEditing] = useState(!me?.siret_status);
  const [status, setStatus] = useState(me?.siret_status || "has_siret");
  const [num, setNum] = useState(me?.siret_number || "");
  const [saving, setSaving] = useState(false);

  const hasNum = !!me?.siret_number;
  const isPending = me?.siret_status === "pending" && !hasNum;

  const save = async () => {
    const cleaned = (num || "").replace(/\D/g, "");
    if (status === "has_siret" && (cleaned.length !== 9 && cleaned.length !== 14)) {
      toast.error("SIRET (14 chiffres) ou SIREN (9 chiffres) attendu.");
      return;
    }
    setSaving(true);
    try {
      const r = await teleproApi.patch("/me/profile", {
        siret_status: status,
        siret_number: status === "has_siret" ? cleaned : "",
      });
      toast.success(status === "has_siret"
        ? "SIRET enregistré. Merci !"
        : "Statut mis à jour. On te relance dès attribution.");
      setEditing(false);
      onUpdated && onUpdated(r.data.telepro);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`border rounded-sm p-4 ${isPending ? "bg-secondary/5 border-secondary/40" : "bg-primary/5 border-primary/20"}`}
      data-testid="telepro-siret-card"
    >
      <div className="flex items-center gap-2 mb-2">
        <Barcode size={16} weight="duotone" className={isPending ? "text-secondary" : "text-primary"}/>
        <div className="font-display font-semibold text-sm">Statut professionnel / SIRET</div>
      </div>

      {!editing ? (
        <div className="space-y-2">
          <div className="text-xs">
            {me?.siret_status === "has_siret" && hasNum ? (
              <span className="inline-flex items-center gap-1.5 text-primary">
                <CheckCircle size={13} weight="fill"/>
                SIRET enregistré :&nbsp;<b className="font-mono text-foreground">{me.siret_number}</b>
              </span>
            ) : me?.siret_status === "pending" ? (
              <span className="inline-flex items-center gap-1.5 text-secondary">
                <Warning size={13} weight="fill"/>
                Immatriculation en cours — pense à transmettre ton SIREN/SIRET dès son attribution.
              </span>
            ) : (
              <span className="text-muted-foreground">Statut non renseigné.</span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-[11px] uppercase tracking-wider font-bold text-primary hover:underline"
            data-testid="telepro-siret-edit"
          >
            {hasNum ? "Modifier" : "Renseigner mon statut"}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className={`flex items-center gap-2 border rounded-sm px-3 py-2 text-xs cursor-pointer ${status === "has_siret" ? "border-primary bg-primary/10" : "border-border"}`}>
              <input type="radio" checked={status === "has_siret"} onChange={() => setStatus("has_siret")} data-testid="telepro-siret-status-has"/>
              <span>J&apos;ai déjà un SIRET</span>
            </label>
            <label className={`flex items-center gap-2 border rounded-sm px-3 py-2 text-xs cursor-pointer ${status === "pending" ? "border-primary bg-primary/10" : "border-border"}`}>
              <input type="radio" checked={status === "pending"} onChange={() => setStatus("pending")} data-testid="telepro-siret-status-pending"/>
              <span>Immatriculation en cours</span>
            </label>
          </div>
          {status === "has_siret" && (
            <input
              type="text"
              value={num}
              onChange={(e) => setNum(e.target.value.replace(/\D/g, "").slice(0, 14))}
              placeholder="Numéro SIRET (14 chiffres) ou SIREN (9)"
              inputMode="numeric"
              className="w-full border border-border rounded-sm px-3 py-2 text-sm bg-background focus:border-primary outline-none font-mono"
              data-testid="telepro-siret-number-input"
            />
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-3 py-2 rounded-sm text-xs font-bold inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="telepro-siret-save"
            >
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
            {me?.siret_status && (
              <button
                type="button"
                onClick={() => { setEditing(false); setStatus(me.siret_status || "has_siret"); setNum(me.siret_number || ""); }}
                className="text-xs text-muted-foreground hover:text-foreground px-3 py-2"
                data-testid="telepro-siret-cancel"
              >
                Annuler
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
